import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import i18next from "src/i18n/config";
import { RootState } from "src/store";
import { makeStyles } from "tss-react/mui";

import { PageContainer } from "@dashboard/shared/components/PageContainer";
import PageHeader from "@dashboard/shared/components/PageHeader";
import { ContentContainer, ContentContainerSubTitle, ContentContainerTitle } from "@dashboard/shared/styles";
import { useCurrentSubscription } from "@dashboard/workspaces/api/helpers";
import { Box, Divider, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import ApmLink from "@shared/ApmLink";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { LoadingBox } from "@shared/Loading";

import { selectDevicesByGroupIds } from "../devices/store/selectors/devices";

import DownloadsTable from "./components/DownloadsTable";
import { useReleaseFiles } from "./hooks/useReleaseFiles";

const useStyles = makeStyles()((theme) => ({
    row: {
        display: "flex",
        padding: theme.spacing(2, 1),
    },
    logoBox: {
        width: 150,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
    },
    li: { marginBottom: theme.spacing(1), display: "flex", alignItems: "center", gap: theme.spacing(1) },
    listBox: {
        marginLeft: theme.spacing(9),
    },
    tutorialList: { listStyleType: "disc", marginLeft: theme.spacing(6), marginTop: theme.spacing(2) },
    tutorialAlert: { marginBottom: theme.spacing(4) },
    warningAlert: { marginTop: theme.spacing(4) },
}));

const DownloadsPage = () => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages"]);
    const { data: currentSubscription } = useCurrentSubscription({});
    const groupIds = currentSubscription?.groupIds || [];
    const devices = useSelector((state: RootState) => selectDevicesByGroupIds(state, groupIds));
    const releaseFiles = useReleaseFiles();

    return (
        <PageContainer>
            <PageHeader
                title={t("pages:downloadsPage.title")}
                iconName="fa-regular fa-floppy-disk"
                description={t("pages:downloadsPage.description")}
            />
            <ExpiredWrapper>
                <ContentContainer>
                    <ContentContainerTitle variant="subtitle1">
                        {t("pages:downloadsPage.section.installers.title")}
                    </ContentContainerTitle>
                    <ContentContainerSubTitle>{t("pages:downloadsPage.section.installers.description")}</ContentContainerSubTitle>

                    <CustomAlert severity="warning" className={classes.warningAlert}>
                        <Typography>{t("pages:downloadsPage.alert.deprecatedInstaller")}</Typography>
                    </CustomAlert>

                    <Box className={classes.row}>
                        <Box className={classes.logoBox}>
                            <i className="fab fa-3x fa-linux" />
                            <Typography variant="subtitle1">{t("pages:downloadsPage.installer.linux.title")}</Typography>
                        </Box>
                        <Box className={classes.listBox}>
                            <ul>
                                <li className={classes.li}>
                                    <Typography>{t("pages:downloadsPage.installer.linux.version")}</Typography>
                                </li>
                                <li className={classes.li}>
                                    <Typography>{t("pages:downloadsPage.installer.linux.size")}</Typography>
                                </li>
                                <li className={classes.li}>
                                    <Typography>{t("pages:downloadsPage.installer.linux.download")}</Typography>
                                    <ApmLink
                                        parent="Downloads page"
                                        title="Download installer"
                                        href="https://s3.emteria.com/public/installer/Emteria.OS_Installer-v1.1-43.AppImage"
                                    >
                                        {/* eslint-disable-next-line i18next/no-literal-string */}
                                        <Typography>Emteria.OS_Installer-v1.1-43.AppImage</Typography>
                                    </ApmLink>
                                </li>
                            </ul>
                        </Box>
                    </Box>
                    <Divider />
                    <Box className={classes.row}>
                        <Box className={classes.logoBox}>
                            <i className="fab fa-3x fa-windows" />
                            <Typography variant="subtitle1">{t("pages:downloadsPage.installer.windows.title")}</Typography>
                        </Box>
                        <Box className={classes.listBox}>
                            <ul>
                                <li className={classes.li}>
                                    <Typography>{t("pages:downloadsPage.installer.windows.version")}</Typography>
                                </li>
                                <li className={classes.li}>
                                    <Typography>{t("pages:downloadsPage.installer.windows.size")}</Typography>
                                </li>
                                <li className={classes.li}>
                                    <Typography>{t("pages:downloadsPage.installer.windows.download")}</Typography>
                                    <ApmLink
                                        parent="Downloads page"
                                        title="Download installer"
                                        href="https://s3.emteria.com/public/installer/Emteria.OS_Installer-v1.1-43.exe"
                                    >
                                        {/* eslint-disable-next-line i18next/no-literal-string */}
                                        <Typography>Emteria.OS_Installer-v1.1-43.exe</Typography>
                                    </ApmLink>
                                </li>
                            </ul>
                        </Box>
                    </Box>
                </ContentContainer>
                <ContentContainer>
                    <ContentContainerTitle variant="subtitle1">
                        {t("pages:downloadsPage.section.androidDownloads.title")}
                    </ContentContainerTitle>
                    <ContentContainerSubTitle>
                        {t("pages:downloadsPage.section.androidDownloads.description")}
                    </ContentContainerSubTitle>

                    {!devices.length ? (
                        <CustomAlert severity="info" className={classes.tutorialAlert}>
                            <Typography>{t("pages:downloadsPage.tutorial.title")}</Typography>
                            <ul className={classes.tutorialList}>
                                <li>
                                    <Typography>
                                        <ApmLink
                                            parent="Downloads page"
                                            title="How to finish device configuration"
                                            href="https://emteria.com/kb/initial-configuration-setup-wizard"
                                        >
                                            {t("pages:downloadsPage.tutorial.items.configuration")}
                                        </ApmLink>
                                    </Typography>
                                </li>
                                <li>
                                    <Typography>
                                        <ApmLink
                                            parent="Downloads page"
                                            title="How to enable remote device management"
                                            href="https://emteria.com/kb/enable-mdm"
                                        >
                                            {t("pages:downloadsPage.tutorial.items.management")}
                                        </ApmLink>
                                    </Typography>
                                </li>
                            </ul>
                        </CustomAlert>
                    ) : null}

                    {releaseFiles.status === "pending" ? (
                        <LoadingBox style={{ minHeight: 300 }} />
                    ) : (
                        <DownloadsTable data={releaseFiles.data} />
                    )}
                </ContentContainer>
            </ExpiredWrapper>
        </PageContainer>
    );
};
export default withMetaDescription(DownloadsPage, {
    title: i18next.t("pages:downloadsPage.metaTitle"),
    description: i18next.t("pages:downloadsPage.metaDescription"),
});
